import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Checkbox } from './checkbox.tsx'
import { Tag } from './tag.tsx'

export interface InsuranceQuoteCardProps
	extends React.ComponentPropsWithoutRef<'div'> {
	/**
	 * Unique identifier for the quote
	 */
	id: string
	/**
	 * Insurance company name
	 */
	company: string
	/**
	 * Company logo URL (optional)
	 */
	companyLogo?: string
	/**
	 * Quote price
	 */
	price: number
	/**
	 * Currency symbol (default: $)
	 */
	currencySymbol?: string
	/**
	 * Locale for number formatting (default: es-AR)
	 */
	locale?: string
	/**
	 * Show recommended badge
	 */
	recommended?: boolean
	/**
	 * Recommended badge text (default: Recomendado)
	 */
	recommendedText?: string
	/**
	 * Show compare checkbox
	 */
	showCompare?: boolean
	/**
	 * Compare checkbox checked state
	 */
	compareChecked?: boolean
	/**
	 * Compare checkbox change handler
	 */
	onCompareChange?: (checked: boolean) => void
	/**
	 * Compare label text (default: Comparar)
	 */
	compareLabel?: string
	/**
	 * Select button text (default: Seleccionar)
	 */
	selectButtonText?: string
	/**
	 * Select button click handler
	 */
	onSelectClick?: () => void
	/**
	 * Show details link
	 */
	showDetailsLink?: boolean
	/**
	 * Details link text (default: Ver más detalles)
	 */
	detailsLinkText?: string
	/**
	 * Details link click handler
	 */
	onDetailsClick?: () => void
	/**
	 * Card selected state
	 */
	selected?: boolean
}

const InsuranceQuoteCard = React.forwardRef<
	HTMLDivElement,
	InsuranceQuoteCardProps
>(
	(
		{
			className,
			id,
			company,
			companyLogo,
			price,
			currencySymbol = '$',
			locale = 'es-AR',
			recommended = false,
			recommendedText = 'Recomendado',
			showCompare = true,
			compareChecked = false,
			onCompareChange,
			compareLabel = 'Comparar',
			selectButtonText = 'Seleccionar',
			onSelectClick,
			showDetailsLink = true,
			detailsLinkText = 'Ver más detalles',
			onDetailsClick,
			selected = false,
			...props
		},
		ref,
	) => {
		return (
			<div
				ref={ref}
				className={cn(
					'relative flex flex-col gap-4 rounded-xl border bg-white p-4 transition-all',
					selected
						? 'border-blue-500 shadow-lg'
						: 'border-neutral-300 hover:border-neutral-400',
					className,
				)}
				{...props}
			>
				{/* Recommended badge */}
				{recommended && (
					<Tag
						variant="secondary"
						appearance="solid"
						color="green"
						size="s"
						corner="rounded"
						option="text-icon-left"
						leftIcon="check"
						className="absolute top-[-10px] right-1/2 translate-x-1/2"
					>
						{recommendedText}
					</Tag>
				)}

				{/* Company logo */}
				<div className="flex h-[80px] items-center justify-center rounded-lg bg-neutral-100">
					{companyLogo ? (
						<img
							src={companyLogo}
							alt={company}
							className="max-h-[60px] max-w-[140px] object-contain"
						/>
					) : (
						<p className="font-parkinsans text-xl font-bold text-blue-800">
							{company}
						</p>
					)}
				</div>

				{/* Price */}
				<div className="flex flex-col items-center gap-1">
					<p className="font-parkinsans text-[32px] leading-10 font-medium text-blue-500">
						{currencySymbol} {price.toLocaleString(locale)}
					</p>
				</div>

				{/* Actions */}
				<div className="flex flex-col gap-2">
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						className="w-full rounded-lg"
						onClick={onSelectClick}
					>
						{selectButtonText}
					</Button>
					{showDetailsLink && (
						<button
							type="button"
							onClick={onDetailsClick}
							className="font-parkinsans text-sm leading-5 font-normal text-blue-500 underline transition-colors hover:text-blue-600"
						>
							{detailsLinkText}
						</button>
					)}
				</div>

				{/* Compare checkbox */}
				{showCompare && (
					<div className="flex items-center justify-center gap-2">
						<Checkbox
							id={`compare-${id}`}
							checked={compareChecked}
							onCheckedChange={onCompareChange}
						/>
						<label
							htmlFor={`compare-${id}`}
							onClick={() => onCompareChange?.(!compareChecked)}
							className="font-parkinsans cursor-pointer text-sm leading-5 font-normal text-blue-800"
						>
							{compareLabel}
						</label>
					</div>
				)}
			</div>
		)
	},
)

InsuranceQuoteCard.displayName = 'InsuranceQuoteCard'

export { InsuranceQuoteCard }
